/**
 * Pipelines Controller — State management and gateway RPC calls for pipelines.
 */

import type {
  PipelineEntry,
  PipelinesAdminProps,
  PipelineRunEntry,
  PipelineStepRunEntry,
} from "../views/pipelines-admin.ts";

// ────────────────────────────────────────────────────────────
// State
// ────────────────────────────────────────────────────────────

export interface PipelinesState {
  client: { request<T>(method: string, params: unknown): Promise<T> } | null;
  connected: boolean;
  pipelinesList: PipelineEntry[];
  pipelinesLoading: boolean;
  pipelinesError: string | null;
  pipelinesCreateMode: boolean;
  pipelinesEditingId: string | null;
  pipelinesForm: Partial<PipelineEntry> | null;
  pipelinesRunResult: {
    runId: string;
    status: string;
    outputs: Record<string, unknown>;
    error?: string;
  } | null;
  pipelinesRunLoading: boolean;
  pipelinesRunStatus: { run: PipelineRunEntry | null; steps: PipelineStepRunEntry[] } | null;
  pipelinesFilterProjectId: string | null;
  // We also need projects for the filter dropdown
  agentProjects: Array<{ id: string; name: string; [k: string]: unknown }>;
}

export const PIPELINES_STATE_DEFAULTS = {
  pipelinesList: [] as PipelineEntry[],
  pipelinesLoading: false,
  pipelinesError: null as string | null,
  pipelinesCreateMode: false,
  pipelinesEditingId: null as string | null,
  pipelinesForm: null as Partial<PipelineEntry> | null,
  pipelinesRunResult: null as PipelinesState["pipelinesRunResult"],
  pipelinesRunLoading: false,
  pipelinesRunStatus: null as PipelinesState["pipelinesRunStatus"],
  pipelinesFilterProjectId: null as string | null,
};

// ────────────────────────────────────────────────────────────
// CRUD Operations
// ────────────────────────────────────────────────────────────

type ListResult = { ok: boolean; pipelines: PipelineEntry[] };
type SingleResult = { ok: boolean; pipeline?: PipelineEntry; error?: string };
type DeleteResult = { ok: boolean };

export async function loadPipelines(state: PipelinesState): Promise<void> {
  if (!state.client) {
    return;
  }
  state.pipelinesLoading = true;
  state.pipelinesError = null;
  try {
    const params: Record<string, unknown> = { activeOnly: false };
    if (state.pipelinesFilterProjectId) {
      params.projectId = state.pipelinesFilterProjectId;
    }
    const res = await state.client.request<ListResult>("igloobot.pipelines.list", params);
    state.pipelinesList = res.pipelines ?? [];
  } catch (err) {
    state.pipelinesError = String(err);
  } finally {
    state.pipelinesLoading = false;
  }
}

export async function savePipeline(state: PipelinesState): Promise<void> {
  if (!state.client || !state.pipelinesForm) {
    return;
  }
  try {
    if (state.pipelinesEditingId) {
      await state.client.request<SingleResult>("igloobot.pipelines.update", {
        id: state.pipelinesEditingId,
        ...state.pipelinesForm,
      });
    } else {
      await state.client.request<SingleResult>("igloobot.pipelines.create", state.pipelinesForm);
    }
    state.pipelinesForm = null;
    state.pipelinesCreateMode = false;
    state.pipelinesEditingId = null;
    await loadPipelines(state);
  } catch (err) {
    state.pipelinesError = String(err);
  }
}

export async function deletePipeline(state: PipelinesState, id: string): Promise<void> {
  if (!state.client) {
    return;
  }
  try {
    await state.client.request<DeleteResult>("igloobot.pipelines.delete", { id });
    await loadPipelines(state);
  } catch (err) {
    state.pipelinesError = String(err);
  }
}

export async function togglePipelineActive(
  state: PipelinesState,
  id: string,
  isActive: boolean,
): Promise<void> {
  if (!state.client) {
    return;
  }
  try {
    await state.client.request<SingleResult>("igloobot.pipelines.update", { id, isActive });
    await loadPipelines(state);
  } catch (err) {
    state.pipelinesError = String(err);
  }
}

// ────────────────────────────────────────────────────────────
// Run Operations
// ────────────────────────────────────────────────────────────

type RunResult = {
  ok: boolean;
  runId: string;
  status: string;
  outputs: Record<string, unknown>;
  error?: string;
};
type StatusResult = { ok: boolean; run: PipelineRunEntry | null; steps: PipelineStepRunEntry[] };

export async function runPipelineAction(state: PipelinesState, pipelineId: string): Promise<void> {
  if (!state.client) {
    return;
  }
  state.pipelinesRunLoading = true;
  state.pipelinesRunResult = null;
  try {
    const res = await state.client.request<RunResult>("igloobot.pipelines.run", {
      pipelineId,
      projectId: state.pipelinesFilterProjectId ?? "",
      agentId: "dev",
      inputs: {},
    });
    state.pipelinesRunResult = {
      runId: res.runId,
      status: res.status,
      outputs: res.outputs,
      error: res.error,
    };
  } catch (err) {
    state.pipelinesError = String(err);
  } finally {
    state.pipelinesRunLoading = false;
  }
}

export async function checkRunStatus(state: PipelinesState, runId: string): Promise<void> {
  if (!state.client) {
    return;
  }
  try {
    const res = await state.client.request<StatusResult>("igloobot.pipelines.status", { runId });
    state.pipelinesRunStatus = { run: res.run ?? null, steps: res.steps ?? [] };
  } catch (err) {
    state.pipelinesError = String(err);
  }
}

// ────────────────────────────────────────────────────────────
// Form Helpers
// ────────────────────────────────────────────────────────────

export function startCreatePipeline(state: PipelinesState): void {
  state.pipelinesCreateMode = true;
  state.pipelinesEditingId = null;
  state.pipelinesForm = { name: "", description: "", steps: [], inputs: [], outputs: [] };
}

export function startEditPipeline(state: PipelinesState, id: string): void {
  const pipeline = state.pipelinesList.find((p) => p.id === id);
  if (!pipeline) {
    return;
  }
  state.pipelinesCreateMode = false;
  state.pipelinesEditingId = id;
  state.pipelinesForm = { ...pipeline };
}

export function cancelPipelineForm(state: PipelinesState): void {
  state.pipelinesCreateMode = false;
  state.pipelinesEditingId = null;
  state.pipelinesForm = null;
}

export function updatePipelineForm(state: PipelinesState, field: string, value: unknown): void {
  if (!state.pipelinesForm) {
    return;
  }
  state.pipelinesForm = { ...state.pipelinesForm, [field]: value };
}

// ────────────────────────────────────────────────────────────
// Event Handlers
// ────────────────────────────────────────────────────────────

export function handlePipelineProgressEvent(state: PipelinesState, payload: any): void {
  if (!state.pipelinesRunStatus) {
    return;
  }

  if (payload.type?.startsWith("step:")) {
    const existingStepIndex = state.pipelinesRunStatus.steps.findIndex(
      (s) => s.stepId === payload.stepId,
    );
    if (existingStepIndex >= 0) {
      const existing = state.pipelinesRunStatus.steps[existingStepIndex];
      const updated = { ...existing };

      if (payload.type === "step:completed") {
        updated.status = "completed";
        updated.outputs = payload.outputs;
      } else if (payload.type === "step:failed") {
        updated.status = "failed";
        updated.error = payload.error;
      } else if (payload.type === "step:started") {
        updated.status = "running";
      }

      const newSteps = [...state.pipelinesRunStatus.steps];
      newSteps[existingStepIndex] = updated;
      state.pipelinesRunStatus = { ...state.pipelinesRunStatus, steps: newSteps };
    } else {
      // Step doesn't exist yet, we probably didn't do a full status check
      state.pipelinesRunStatus = {
        ...state.pipelinesRunStatus,
        steps: [
          ...state.pipelinesRunStatus.steps,
          {
            id: payload.stepId,
            pipelineRunId: payload.runId,
            stepId: payload.stepId,
            skillName: payload.skillName,
            status:
              payload.type === "step:completed"
                ? "completed"
                : payload.type === "step:failed"
                  ? "failed"
                  : "running",
            inputs: {},
            outputs: payload.outputs ?? {},
            error: payload.error ?? null,
            attempt: 1,
            startedAt: new Date().toISOString(),
            completedAt: null,
          } as any,
        ],
      };
    }
  } else if (payload.type === "pipeline:completed" || payload.type === "pipeline:failed") {
    if (state.pipelinesRunStatus.run) {
      state.pipelinesRunStatus = {
        ...state.pipelinesRunStatus,
        run: {
          ...state.pipelinesRunStatus.run,
          status: payload.type === "pipeline:completed" ? "completed" : "failed",
          outputs: payload.outputs ?? state.pipelinesRunStatus.run.outputs,
          error: payload.error ?? state.pipelinesRunStatus.run.error,
        },
      };
    }
  }
}

// ────────────────────────────────────────────────────────────
// Props Builder
// ────────────────────────────────────────────────────────────

export function buildPipelinesProps(state: PipelinesState): PipelinesAdminProps {
  return {
    pipelines: state.pipelinesList,
    loading: state.pipelinesLoading,
    error: state.pipelinesError,
    createMode: state.pipelinesCreateMode,
    editingId: state.pipelinesEditingId,
    form: state.pipelinesForm,
    runResult: state.pipelinesRunResult,
    runLoading: state.pipelinesRunLoading,
    runStatusResult: state.pipelinesRunStatus,
    projects: (state.agentProjects ?? []).map((p) => ({ id: p.id, name: p.name })),
    filterProjectId: state.pipelinesFilterProjectId,
    onRefresh: () => void loadPipelines(state),
    onStartCreate: () => startCreatePipeline(state),
    onStartEdit: (id) => startEditPipeline(state, id),
    onCancelForm: () => cancelPipelineForm(state),
    onUpdateForm: (field, value) => updatePipelineForm(state, field, value),
    onSave: () => void savePipeline(state),
    onDelete: (id) => void deletePipeline(state, id),
    onToggleActive: (id, active) => void togglePipelineActive(state, id, active),
    onRun: (id) => void runPipelineAction(state, id),
    onCheckStatus: (runId) => void checkRunStatus(state, runId),
    onFilterProject: (projectId) => {
      state.pipelinesFilterProjectId = projectId;
      void loadPipelines(state);
    },
  };
}
